import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import ErrorResponse, { ErrorType, ErrorTypes } from '../../domain/model/error'
import type { ApiResponse, ServiceResponse } from '../model/service'

export function errorByStatus(status?: number): ErrorType {
  switch (status) {
    case 400: return ErrorTypes.INVALID_ARGS
    case 401: return ErrorTypes.LOGIN_REQUIRED
    case 403: return ErrorTypes.FORBIDDEN
    case 404: return ErrorTypes.NO_DATA
    case 412: return ErrorTypes.INVALID_ARGS
    case 500: return ErrorTypes.INTERNAL
    default: return ErrorTypes.UNKNOWN
  }
}

export async function wrap(f: () => Promise<ApiResponse>): Promise<ServiceResponse> {
  try {
    return await f()
  } catch (e) {
    /* Check if error is axios error */
    const er = (e as AxiosError).response ? e as AxiosError : null

    /* If not, throw unknown error */
    if (er == null) {
      throw new ErrorResponse(
        ErrorTypes.UNKNOWN,
        e as Error
      )
    }

    /* Throw a formatted error */
    throw new ErrorResponse(
      errorByStatus(er.response?.status),
      e as Error,
      er.message,
      er.response?.status
    )
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const get = (
  http: AxiosInstance,
  url: string,
  params?: Record<string, any>
): Promise<ServiceResponse> => wrap(() => {
  return http.get(url, { params })
})

export const post = (
  http: AxiosInstance,
  url: string,
  data?: Record<string, any>,
  config?: AxiosRequestConfig,
  urlAppend?: string
): Promise<ServiceResponse> => wrap(() => {
  return http.post(urlAppend ? url + urlAppend : url, data, config)
})

export const put = (
  http: AxiosInstance,
  url: string,
  data?: Record<string, any>,
  config?: AxiosRequestConfig,
  urlAppend?: string
): Promise<ServiceResponse> => wrap(() => {
  return http.put(urlAppend ? url + urlAppend : url, data, config)
})

export const _delete = (
  http: AxiosInstance,
  url: string,
  data?: Record<string, any>,
  config?: AxiosRequestConfig,
  urlAppend?: string
): Promise<ServiceResponse> => wrap(() => {
  return http.delete(urlAppend ? url + urlAppend : url, { data, ...config })
})
